/**
 * useElementWidth Hook
 *
 * Tracks an element's pixel width via ResizeObserver and exposes
 * an `isNarrow` flag for responsive rendering at small sizes.
 *
 * Used by simple field preview components (Text, Integer, Decimal,
 * Fraction, Boolean, Range) to adapt layout at small sizes.
 * Threshold is border-box (284px = 250px content + 34px max padding+border).
 */

import { useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useOscillationSafeWidth } from './useOscillationSafeWidth';

const NARROW_THRESHOLD = 284;

interface UseElementWidthReturn {
  containerRef: React.RefCallback<HTMLElement>;
  containerWidth: number;
  isNarrow: boolean;
}

export const useElementWidth = (): UseElementWidthReturn => {
  // Oscillation-safe: a narrow/wide layout switch that changes our own
  // measured width must not loop (React error #185).
  const [containerWidth, updateContainerWidth] = useOscillationSafeWidth();
  const observerRef = useRef<ResizeObserver | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  // Synchronous measurement before paint — catches state-driven width changes
  // immediately so responsive breakpoints are correct on the first paint frame.
  useLayoutEffect(() => {
    if (nodeRef.current) {
      updateContainerWidth(nodeRef.current.getBoundingClientRect().width);
    }
  });

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Ref callback — handles element mount/unmount and observer lifecycle
  const containerRef = useCallback((node: HTMLElement | null) => {
    // Disconnect previous observer if element changes
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    nodeRef.current = node;

    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          updateContainerWidth(entry.target.getBoundingClientRect().width);
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, [updateContainerWidth]);

  const isNarrow = containerWidth > 0 && containerWidth < NARROW_THRESHOLD;

  return { containerRef, containerWidth, isNarrow };
};

export default useElementWidth;
